import cx from 'classnames';
import { Fragment, useEffect, useRef } from 'react';
import { useLocation } from 'wouter';

import AccountMenu from 'components/AccountMenu';
import Icon from 'components/Icon/Icon';
import NavigationMenu from 'components/NavigationMenu';
import ResponsibleGamingMenu from 'components/ResponsibleGamingMenu';
import Settings from 'components/Settings';
import SupportMenu from 'components/SupportMenu';
import { AuthStatus, useClientProfile, useUISettings } from 'store';
import useLayout, { LayoutType } from 'utils/layout';
import { useBasicRoutes } from 'utils/location';

import styles from './SandwichMenu.module.scss';

const SandwichMenu = () => {
  const [{ showSandwichMenu }, { toggleSandwichMenu }] = useUISettings();
  const [{ status }] = useClientProfile();
  const [location] = useLocation();
  const layout = useLayout();
  const shouldRender = layout === LayoutType.mobile;
  const [basicRouteName, { isAccount, isStatic }] = useBasicRoutes();
  const shouldRenderNavigationMenu = !isAccount && !isStatic;
  const shouldRenderAccountMenu = status === AuthStatus.signedIn && !isStatic;

  // this ref used in the hook that should react only on location change
  // for preventing infinite loop with showSandwichMenu as dependency
  const showSandwichMenuRef = useRef(showSandwichMenu);

  useEffect(() => {
    document.body.style.overflow = showSandwichMenu ? 'hidden' : 'auto';
    showSandwichMenuRef.current = showSandwichMenu;
  }, [showSandwichMenu]);

  useEffect(() => {
    // this effect should close the menu and restore scroll on location change
    void location;

    if (!showSandwichMenuRef.current) {
      return;
    }

    document.body.style.overflow = 'auto';
    toggleSandwichMenu();
  }, [location, toggleSandwichMenu]);

  return (
    shouldRender && (
      <>
        {showSandwichMenu && (
          // biome-ignore lint/a11y/useKeyWithClickEvents: this backdrop rendered only for mobile where no keyboard events
          <div
            className={styles['sandwich-menu-backdrop']}
            onClick={toggleSandwichMenu}
          />
        )}
        <div
          className={cx(styles['sandwich-menu'], styles[basicRouteName], {
            [styles.show]: showSandwichMenu,
          })}
        >
          {showSandwichMenu && (
            <>
              <button
                type="button"
                className={styles['close-button']}
                onClick={toggleSandwichMenu}
                data-testid="close-button"
              >
                <Icon name="close.outline" width={24} height={24} />
              </button>

              <div className={styles['sandwich-menu-content']}>
                {shouldRenderNavigationMenu && <NavigationMenu />}
                {shouldRenderAccountMenu && (
                  <Fragment>
                    <AccountMenu asAccordion={!isAccount} />
                    <ResponsibleGamingMenu />
                  </Fragment>
                )}
                <Settings />
                <SupportMenu asAccordion={!isStatic} />
              </div>
            </>
          )}
        </div>
      </>
    )
  );
};

export default SandwichMenu;
